import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { omit } from 'lodash-es';
import { b2bServices, bookingsServices } from '@services';
import { getCleanedUrlParams, showError } from '@utils';
import { QUERY_KEYS } from 'constants/queryKeys';
import { URLS } from 'layouts/DrawerLayout/constants';
import { getBookingsPayload } from 'hooks/bookings/utils';
import { useLayout } from 'hooks/layout';
import { normalizeBookings } from './utils';

export const B2B_ORDER_OPERATIONS_PAGE_SIZE = 200;

export const useB2BOrderOperations = () => {
  const router = useRouter();
  const { tenant } = useLayout();

  const b2bOrderId = router.query?.b2bOrderId;
  const page = router.query?.page || 1;
  const pageSize = router.query?.pageSize || B2B_ORDER_OPERATIONS_PAGE_SIZE;
  const order = router.query?.order || 'desc';
  const orderBy = router.query?.orderBy || 'created_at';
  const contractPeriod = router.query?.contractPeriod;
  const stage = router.query?.stage;
  const query = router.query?.query;

  const cleanParams = getCleanedUrlParams({
    page,
    orderBy,
    order,
    tenant,
    pageSize: parseInt(pageSize, 10) || B2B_ORDER_OPERATIONS_PAGE_SIZE,
    contractPeriod,
    stage,
    query,
    b2bOrderId,
  });
  const b2bOrderResponse = useQuery(
    [QUERY_KEYS.GET_B2B_ORDER_KEY, b2bOrderId],
    () => b2bServices.getB2BOrder(b2bOrderId),
    { enabled: !!b2bOrderId }
  );

  const bookingsResponse = useQuery(
    [QUERY_KEYS.GET_B2B_ORDER_BOOKINGS_KEY, cleanParams],
    () => {
      const params = {
        page,
        orderBy,
        order,
        pageSize: parseInt(pageSize, 10) || B2B_ORDER_OPERATIONS_PAGE_SIZE,
        query,
      };
      const payload = getBookingsPayload({
        ...omit(
          cleanParams,
          'b2bOrderId',
          'page',
          'orderBy',
          'order',
          'pageSize',
          'query'
        ),
        'ticket_details.order_id': `${b2bOrderId}`,
      });
      return bookingsServices.getBookings({
        params: getCleanedUrlParams(params),
        payload,
      });
    },
    { enabled: !!b2bOrderId }
  );

  const enumsResponse = useQuery(
    [QUERY_KEYS.GET_BOOKINGS_ENUMS_KEY],
    () => bookingsServices.getBookingsEnums(),
    { enabled: router.isReady }
  );

  const handleClickBack = () => {
    router.push(URLS.b2b);
  };

  const b2bOrder = b2bOrderResponse?.data?.data;
  const bookings = normalizeBookings(bookingsResponse?.data?.data || []);

  useEffect(() => {
    if (bookingsResponse.isError) {
      showError(bookingsResponse?.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingsResponse.isError]);

  return {
    page,
    pageSize: parseInt(pageSize, 10) || B2B_ORDER_OPERATIONS_PAGE_SIZE,
    b2bOrderId,
    total: bookingsResponse?.data?.meta?.total,
    loading: bookingsResponse?.isLoading,
    bookings,
    b2bOrder,
    enums: enumsResponse?.data?.data || {},
    refetch: bookingsResponse.refetch,
    handleClickBack,
  };
};
